// Display: how large Lantern's text is, and how close together messages and
// lists sit. Both are the account's, set on every device, and kept in this
// browser too, so the page starts in them before signing in. applyDisplay
// sets --text-scale and data-density on <html>; the build multiplies every
// font size and line height in the CSS by --text-scale (vite.config.ts).

export type TextSize = "" | "small" | "large" | "larger";
export type Density = "" | "compact";
// Layout is how messages sit: "" in one column, as Slack has them, or
// "bubbles", one's own on the right, as WeChat, Feishu and Teams' chats do.
export type Layout = "" | "bubbles";

// textSizes are the sizes there are, the same ids as the server's, with ""
// the default, and how much larger than it each is.
export const textSizes: [TextSize, number][] = [
  ["small", 0.9],
  ["", 1],
  ["large", 1.1],
  ["larger", 1.25],
];

const sizeKey = "lantern.text-size";
const densityKey = "lantern.density";
const layoutKey = "lantern.message-layout";

const read = (k: string) => {
  try {
    return localStorage.getItem(k) ?? "";
  } catch {
    return "";
  }
};
const write = (k: string, v: string) => {
  try {
    if (v) localStorage.setItem(k, v);
    else localStorage.removeItem(k);
  } catch {
    // It still applies until the page reloads.
  }
};

const isSize = (v: string): v is TextSize => textSizes.some(([id]) => id === v);

let size: TextSize = "";
let density: Density = "";
let layout: Layout = "";
{
  const s = read(sizeKey);
  size = isSize(s) ? s : "";
  density = read(densityKey) === "compact" ? "compact" : "";
  layout = read(layoutKey) === "bubbles" ? "bubbles" : "";
}

export function textSize(): TextSize {
  return size;
}

export function densityInUse(): Density {
  return density;
}

export function layoutInUse(): Layout {
  return layout;
}

// display shows a text size and density at once, as chosen or as the
// account has them.
export function display(p: { text_size?: string; density?: string; message_layout?: string }) {
  size = isSize(p.text_size ?? "") ? (p.text_size as TextSize) : "";
  density = p.density === "compact" ? "compact" : "";
  if (p.message_layout !== undefined) layout = p.message_layout === "bubbles" ? "bubbles" : "";
  applyDisplay();
}

// onDisplay calls back when either changes, for what draws them itself.
const listeners = new Set<() => void>();
export function onDisplay(f: () => void): () => void {
  listeners.add(f);
  return () => listeners.delete(f);
}

export function applyDisplay() {
  const root = document.documentElement;
  write(sizeKey, size);
  write(densityKey, density);
  write(layoutKey, layout);
  const scale = textSizes.find(([id]) => id === size)![1];
  if (scale === 1) root.style.removeProperty("--text-scale");
  else root.style.setProperty("--text-scale", String(scale));
  if (density) root.dataset.density = density;
  else delete root.dataset.density;
  if (layout) root.dataset.layout = layout;
  else delete root.dataset.layout;
  for (const f of listeners) f();
}
